import { FC, FormEvent, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import { AsyncSearch, Loader, SearchableSelect } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import { useSelectedLocation } from 'contexts';
import useCustomerCarrierApi from 'hooks/use-location-carrier-api';
import { searchTruckingCompanies } from 'services';
import { Location, TruckingCompany } from 'types';
import { formatTruckingCompany } from 'utils/format';

import LockableDateInput from 'components/inputs/date/lockable';
import dayjs from 'dayjs';
import './rule.scss';

const NewLocationRule: FC = () => {
  const navigate = useNavigate();

  const { createRule } = useCustomerCarrierApi();
  const { locations } = useSelectedLocation();

  const [loading, setLoading] = useState<boolean>(false);

  const [location, setLocation] = useState<Location>();
  const [truckingCompany, setTruckingCompany] = useState<TruckingCompany>();
  const [gateInAllowed, setGateInAllowed] = useState<boolean>(false);
  const [gateOutAllowed, setGateOutAllowed] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>();
  const [notes, setNotes] = useState<string>('');

  const handleReset = useCallback(() => navigate('/scac-management/location-rules', { replace: true }), [navigate]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setLoading(true);
      const response = await createRule({ location, truckingCompany, gateInAllowed, gateOutAllowed, startDate, notes });
      if (response) {
        navigate('/scac-management/location-rules', { replace: true });
      }
      setLoading(false);
    },
    [location, truckingCompany, gateInAllowed, gateOutAllowed, startDate, notes, createRule, navigate, setLoading]
  );

  return (
    <Loader loading={loading}>
      <ErrorDialog />
      <div id="scac-management" className="page">
        <div className="page-header">
          <h1>SCAC MANAGEMENT</h1>
        </div>
        <div className="page-content h-100">
          <form
            id="location-carrier-rule"
            className="panel h-100"
            onReset={handleReset}
            onSubmit={handleSubmit}
            data-cy="location-rule-form"
          >
            <div className="d-flex align-items-center justify-content-between">
              <h4>ADD LOCATION RULE</h4>
              <div className="grid-2">
                <button className="btn-outline" type="reset">
                  CANCEL
                </button>
                <button className="btn-primary" type="submit">
                  SUBMIT
                </button>
              </div>
            </div>
            <div className="card-primary">
              <div className="card-header mb-5" />
              <div className="card-body grid-auto auto-rows">
                <SearchableSelect
                  required
                  containerClass="text-field"
                  inputClass={!location ? 'blank' : ''}
                  label="LOCATION"
                  onValueSelected={locationId => {
                    setLocation(locations.find(({ id }) => id === Number(locationId)));
                  }}
                  options={locations.map(({ id, name }) => ({ label: name, value: `${id}` }))}
                  value={`${location?.id ?? ''}`}
                  data-cy="location-input"
                />
                <AsyncSearch<TruckingCompany>
                  required
                  label="CARRIER"
                  formatValue={formatTruckingCompany}
                  handleSelect={setTruckingCompany}
                  search={searchTruckingCompanies}
                  value={truckingCompany}
                  data-cy="carrier-search"
                />
                <LockableDateInput
                  date={startDate}
                  setDate={setStartDate}
                  label="START DATE"
                  min={dayjs().toISOString()}
                  data-cy="start-date-input"
                />
                <div className="text-field">
                  <input
                    className={`h-100 w-100 ${!notes ? 'blank' : ''}`}
                    id="notes-input"
                    name="notes"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    data-cy="notes-input"
                  />
                  <label htmlFor="notes-input">NOTES</label>
                </div>
                <div className="grid-2 mt-2">
                  <div className="text-field">
                    <input
                      id="gate-in-checkbox"
                      type="checkbox"
                      checked={gateInAllowed}
                      onChange={e => setGateInAllowed(e.target.checked)}
                      data-cy="allow-gate-in-checkbox"
                    />
                    <label htmlFor="gate-in-checkbox">ALLOW GATE IN</label>
                  </div>
                  <div className="text-field">
                    <input
                      id="gate-out-checkbox"
                      type="checkbox"
                      checked={gateOutAllowed}
                      onChange={e => setGateOutAllowed(e.target.checked)}
                      data-cy="allow-gate-out-checkbox"
                    />
                    <label htmlFor="gate-out-checkbox">ALLOW GATE OUT</label>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Loader>
  );
};

export default NewLocationRule;
